// Bundles the whole prayer treasury into one printable home booklet, with each prayer's
// sacred image, title, origin, full text (all languages) and source — plus a cover and contents.

import { scripturePrintNotice } from "@/data/scripture";

const esc = (v) =>
  String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
const abs = (src) => (!src ? "" : /^https?:\/\//.test(src) ? src : `${window.location.origin}${src}`);

const blockHtml = (b) =>
  `<div class="blk"${b.dir === "rtl" ? ' dir="rtl"' : ""}>
     ${b.label ? `<p class="lang">${esc(b.label)}</p>` : ""}
     <p class="body${b.dir === "rtl" ? " rtl" : ""}${b.italic ? " italic" : ""}">${esc(b.text).replace(/\n/g, "<br>")}</p>
   </div>`;

const prayerHtml = (p, n, sectionIndex) => {
  const img = abs(p.image?.src);
  let bodyContent;
  if (p.blocks && p.blocks.length) bodyContent = `<div class="blocks">${p.blocks.map(blockHtml).join("")}</div>`;
  else if (p.guide) bodyContent = `<p class="body">Pray the full Rosary — the four sets of mysteries and every prayer — with the interactive guide at theotokosmaterdei.com/rosary.</p>`;
  else if (p.source?.url) bodyContent = `<p class="body">The full text is published here: <a href="${esc(p.source.url)}">${esc(p.source.label || p.source.url)}</a></p>`;
  else bodyContent = "";
  return `<article class="prayer">
    <div class="phead">
      ${img ? `<figure class="pimg"><img src="${esc(img)}" alt="${esc(p.image.alt || p.title)}"></figure>` : ""}
      <div class="pmeta">
        ${p.tag ? `<p class="ptag">${esc(p.tag)}</p>` : ""}
        <h3 class="ptitle">${esc(p.title)}</h3>
        ${p.subtitle ? `<p class="psub">${esc(p.subtitle)}</p>` : ""}
      </div>
    </div>
    ${bodyContent}
    ${p.note ? `<p class="pnote">${esc(p.note)}</p>` : ""}
    ${p.source?.url && p.blocks?.length ? `<p class="psrc"><a href="${esc(p.source.url)}">${esc(p.source.label || "Source")}</a></p>` : ""}
    ${scripturePrintNotice(p) ? `<p class="pnote" data-testid="booklet-scripture-copyright-${sectionIndex}-${n}">${esc(scripturePrintNotice(p))}</p>` : ""}
  </article>`;
};

const sectionHtml = (s, sectionIndex) =>
  `<section class="chapter">
     <div class="chapter-head"><span class="rule"></span><h2>${esc(s.heading)}</h2><span class="rule"></span></div>
     ${s.prayers.map((p, n) => prayerHtml(p, n, sectionIndex)).join("")}
   </section>`;

const tocHtml = (sections) =>
  `<nav class="toc">
     <h2>Contents</h2>
     ${sections.map((s) => `<div class="toc-sec"><p class="toc-h">${esc(s.heading)}</p><ul>${s.prayers.map((p) => `<li>${esc(p.title)}</li>`).join("")}</ul></div>`).join("")}
   </nav>`;

const documentHtml = (sections, meta) => {
  const cover = abs(meta.coverImage);
  return `<!doctype html><html lang="en"><head><meta charset="utf-8">
  <meta name="viewport" content="width=device-width,initial-scale=1"><title>${esc(meta.title)}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,500;0,600;1,500&family=Source+Sans+3:wght@400;600&display=swap" rel="stylesheet">
  <style>
    :root{--midnight:#102a43;--marian:#1f4e79;--gold:#b8860b;--slate:#5b6c86;--ivory:#fbf9f4;}
    *{box-sizing:border-box;}
    html,body{margin:0;padding:0;background:#fff;color:var(--midnight);font-family:'Source Sans 3',system-ui,sans-serif;}
    .cover{height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:48px;page-break-after:always;}
    .cover .eyebrow{font-size:12px;text-transform:uppercase;letter-spacing:.32em;color:var(--gold);font-weight:600;margin:0 0 18px;}
    .cover img{width:260px;max-height:360px;object-fit:contain;border-radius:10px;margin:6px 0 26px;}
    .cover h1{font-family:'Cormorant Garamond',Georgia,serif;font-weight:600;font-size:52px;line-height:1.02;margin:0 0 10px;color:var(--midnight);}
    .cover .sub{font-family:'Cormorant Garamond',Georgia,serif;font-style:italic;font-size:20px;color:var(--slate);margin:0 0 30px;max-width:22em;}
    .cover .site{font-size:11px;text-transform:uppercase;letter-spacing:.26em;color:var(--marian);}
    .content{padding:0 20px;}
    .toc{page-break-after:always;padding:40px 12px;}
    .toc h2{font-family:'Cormorant Garamond',Georgia,serif;font-size:30px;color:var(--marian);border-bottom:2px solid var(--gold);padding-bottom:8px;margin:0 0 20px;}
    .toc-h{font-weight:600;font-size:12px;text-transform:uppercase;letter-spacing:.16em;color:var(--gold);margin:16px 0 4px;}
    .toc ul{margin:0 0 6px;padding-left:18px;columns:2;column-gap:28px;}
    .toc li{font-family:'Cormorant Garamond',Georgia,serif;font-size:14px;line-height:1.5;break-inside:avoid;}
    .chapter{margin:0 0 8px;}
    .chapter-head{display:flex;align-items:center;gap:14px;margin:26px 0 18px;page-break-after:avoid;}
    .chapter-head .rule{flex:1;height:1px;background:linear-gradient(90deg,transparent,var(--gold),transparent);}
    .chapter-head h2{font-family:'Cormorant Garamond',Georgia,serif;font-weight:600;font-size:26px;color:var(--marian);white-space:nowrap;margin:0;}
    .prayer{page-break-inside:avoid;border:1px solid rgba(184,134,11,.28);border-radius:14px;padding:18px 20px;margin:0 0 16px;background:var(--ivory);}
    .phead{display:flex;gap:16px;align-items:flex-start;margin-bottom:10px;}
    .pimg{margin:0;flex:0 0 1.5in;height:1.9in;display:flex;align-items:center;justify-content:center;background:#fff;border:1px solid rgba(31,78,121,.12);border-radius:8px;overflow:hidden;}
    .pimg img{max-width:100%;max-height:100%;object-fit:contain;}
    .pmeta{min-width:0;}
    .ptag{font-size:8.5px;text-transform:uppercase;letter-spacing:.22em;color:var(--gold);font-weight:600;margin:2px 0 6px;}
    .ptitle{font-family:'Cormorant Garamond',Georgia,serif;font-weight:600;font-size:23px;line-height:1.05;margin:0 0 4px;}
    .psub{font-family:'Cormorant Garamond',Georgia,serif;font-style:italic;font-size:13px;color:var(--slate);margin:0;}
    .blocks{display:flex;flex-direction:column;gap:9px;}
    .lang{font-size:8.5px;text-transform:uppercase;letter-spacing:.2em;color:var(--gold);font-weight:600;margin:0 0 3px;}
    .body{font-family:'Cormorant Garamond',Georgia,serif;font-size:14.5px;line-height:1.55;margin:0;white-space:pre-wrap;}
    .body.italic{font-style:italic;}
    .body.rtl{direction:rtl;text-align:right;font-size:16px;}
    .pnote{font-size:9.5px;color:var(--slate);line-height:1.4;margin:10px 0 0;}
    .psrc{font-size:9.5px;margin:6px 0 0;overflow-wrap:anywhere;}
    a{color:var(--marian);}
    .colophon{page-break-before:always;text-align:center;padding:60px 40px;}
    .colophon .cross{color:var(--gold);font-size:28px;}
    .colophon p{font-family:'Cormorant Garamond',Georgia,serif;color:var(--slate);font-size:15px;max-width:26em;margin:14px auto;}
    .print-bar{position:fixed;top:0;left:0;right:0;z-index:9999;display:flex;flex-wrap:wrap;align-items:center;gap:12px;justify-content:center;padding:11px 16px;background:var(--midnight);color:var(--ivory);box-shadow:0 6px 20px rgba(0,0,0,.25);}
    .print-bar .pb-title{font-size:13px;opacity:.85;}
    .print-bar button{font-family:'Source Sans 3',system-ui,sans-serif;cursor:pointer;border-radius:999px;font-size:13px;}
    .print-bar .pb-btn{background:var(--gold);color:#fff;border:none;padding:9px 22px;font-weight:600;}
    .print-bar .pb-btn:hover{background:#caa42a;}
    .print-bar .pb-close{background:transparent;color:var(--ivory);border:1px solid rgba(255,255,255,.45);padding:8px 16px;}
    body{padding-top:62px;}
    @page{margin:14mm;}
    @media print{body{padding-top:0!important;}.print-bar{display:none!important;}}
  </style></head>
  <body data-testid="booklet-body">
    <div class="print-bar" data-testid="print-toolbar">
      <span class="pb-title">Preview — press Print when it looks right</span>
      <button type="button" class="pb-btn" data-testid="print-now-button" onclick="window.print()">Print / Save as PDF</button>
      <button type="button" class="pb-close" data-testid="print-close-button" onclick="window.close()">Close</button>
    </div>
    <div class="cover">
      <p class="eyebrow">${esc(meta.eyebrow || "A home booklet")}</p>
      ${cover ? `<img src="${esc(cover)}" alt="${esc(meta.coverAlt || meta.title)}">` : ""}
      <h1>${esc(meta.title)}</h1>
      <p class="sub">${esc(meta.subtitle || "")}</p>
      <p class="site">Theotokos · Mater Dei — Ad Iesum per Mariam</p>
    </div>
    ${tocHtml(sections)}
    <div class="content">${sections.map(sectionHtml).join("")}</div>
    <div class="colophon"><p class="cross">†</p><p>${esc(meta.colophon || "Prayed at home, this treasury keeps the whole household near the Mother of God. Sources for every prayer are noted so the family may always return to them.")}</p><p class="site">theotokosmaterdei.com</p></div>
  </body></html>`;
};

export function openPrintBooklet(sections, meta = {}) {
  const w = window.open("", "_blank", "width=820,height=1000");
  if (!w) return "Your browser blocked the print window. Please allow pop-ups to print the booklet.";
  w.document.write(documentHtml(sections, meta));
  w.document.close();
  return "";
}
